import { Collapse } from 'antd';
import HeaderMeta from 'components/headerMeta/HeaderMeta';
import ImgDescCard from 'components/ImgDescCard';
import Footer from 'components/layout/Footer';
import {
  ArrowRight,
  ChevronDown,
  Clock,
  Globe2,
  Leaf,
  Plane,
  Route,
  ShieldCheck,
  Star,
} from 'lucide-react';
import { ImageOverlay } from 'pages/charterQuote/components/contents/Content.style';
import FAQAccordion from 'pages/charterQuote/components/contents/FAQAccordion';
import Header from 'pages/charterQuote/components/contents/Header';
import React, { useEffect, useMemo, useState } from 'react';
import {
  buildAbsoluteUrl,
  buildFAQSchema,
  normalizeSchemaDate,
  SITE_URL,
} from 'untils/jsonLdSchemas';
import {
  BottomCta,
  BtnPrimary,
  GroupCharterSplashDivider,
  GroupCharterSplashIntro,
  GroupCharterSplashLead,
  GroupCharterSplashSplit,
  GroupCharterSplashTitle,
  HeroWrapCharter,
  HostAirportTable,
  MobileTableAccordion,
  PageRoot,
  Section,
  StageTag,
  TableWrap,
  WcAirportBriefBlockTitle,
  WcAirportBriefBulletList,
  WcAirportBriefCell,
  WcAirportBriefCosts,
  WcAirportBriefFboCell,
  WcAirportBriefGhost,
  WcAirportBriefGridRow,
  WcAirportBriefHeadRow,
  WcAirportBriefRailIcon,
  WcAirportBriefRailImg,
  WcAirportBriefStack,
  WcAirportBriefText,
  WcFaqTwinFigure,
  WcFaqTwinHeading,
  WcFaqTwinPanel,
  WcFaqTwinRow,
  WcFaqTwinStack,
  WcFlyingCloudsBanner,
  WcPlanningBannerBgAlt,
  WcPlanningBannerSection,
  WcPlanningBannerWrap,
  WcPricingCardColumn,
  WcPricingInteractive,
  WcPricingTierBody,
  WcPricingTierBtn,
  WcPricingTierHeading,
  WcPricingTierPriceStr,
  WcPricingTierSubtitle,
  WcPricingTopRow,
  WcPricingVisual,
  WcQuoteHeroActionSlot,
  WcQuoteHeroBadge,
  WcQuoteHeroBadgeGrid,
  WcQuoteHeroBand,
  WcQuoteHeroBanner,
  WcQuoteHeroHeader,
  WcQuoteHeroHeading,
  WcQuoteHeroLead,
  WcQuoteHeroMainWrap,
  WcQuoteHeroPrimaryCta,
  WcQuoteHeroResultsTop,
  WcQuoteHeroTitleBlock,
  WcRegSplashAccordion,
  WcRegSplashAccordionSpacer,
  WcRegSplashBody,
  WcRegSplashBodyText,
  WcRegSplashChevron,
  WcRegSplashCollapse,
  WcRegSplashCollapseInner,
  WcRegSplashFigure,
  WcRegSplashHead,
  WcRegSplashHeadMain,
  WcRegSplashItem,
  WcRegSplashNumBadge,
  WcRegSplashNumPlain,
  WcRegSplashSplit,
  WcTickerAccessibleText,
  WcTickerLabel,
  WcTickerMarquee,
  WcTickerSegment,
  WcTickerStrip,
  WcTickerTrack,
  WhyPrivateBenefitBody,
  WhyPrivateBenefitCard,
  WhyPrivateBenefitsGrid,
  WhyPrivateBenefitText,
  WhyPrivateBenefitTitle,
  WhyPrivateFleetSection,
} from './WorldCup2026.style';
import {
  BEST_AIRPORTS_WC2026_FBO,
  BEST_AIRPORTS_WC2026_GATEWAYS,
  BEST_AIRPORTS_WC2026_HEADER,
  BEST_AIRPORTS_WC2026_ROWS,
  GROUP_CHARTER_SPLASH_INTRO,
  GROUP_CHARTER_SPLASH_ITEMS,
  GROUP_CHARTER_SPLASH_TITLE,
  HERO_PLAN_FEATURES,
  HOST_AIRPORT_ROWS,
  PRICING_TIERS,
  WC_FAQ_SPLIT_BLOCKS,
  WC_FAQ_SPLIT_PANEL_TITLE,
  WC_PLANNING_BANNER,
  WC_REG_SPLASH_HEADER,
  WC_REG_SPLASH_ITEMS,
  WC_WHEN_BOOK_ACCORDION,
  WC_WHEN_BOOK_INTRO,
  WHY_PRIVATE_CARDS,
} from './worldCupData';

const PAGE_PATH = '/private-jet-charter-world-cup-2026';
const WORLD_CUP_TITLE = 'Private Jet Charter for FIFA World Cup 2026';
const WORLD_CUP_DESCRIPTION =
  'Charter a private jet for FIFA World Cup 2026. Pricing, host city airports, FBO strategy, FAA and TFR regulations, group charter, and when to book across the US, Canada, and Mexico.';
const WORLD_CUP_PUBLISHED_DATE = '2025-11-01';
const DEFAULT_WORLD_CUP_MODIFIED_DATE = '2026-05-22';
const AUTHORITY_LINKS = [
  { href: 'https://tfr.faa.gov', label: 'FAA issues TFRs' },
  {
    href: 'https://www.ecfr.gov/current/title-14/chapter-I/subchapter-G/part-135',
    label: 'FAA Part 135 regulations',
  },
  { href: 'https://www.cbp.gov/travel/air-sea-private/apis', label: 'APIS manifest submission' },
  { href: 'https://tc.canada.ca/en/aviation', label: 'Transport Canada aviation' },
];

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const breadcrumbNavStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '18px',
  fontSize: '13px',
  lineHeight: 1.4,
  color: '#5c6b7a',
};

const breadcrumbLinkStyle = {
  color: '#155dfc',
  textDecoration: 'none',
  fontWeight: 500,
};

const pageMetaStyle = {
  margin: '0 0 16px',
  fontSize: '13px',
  lineHeight: 1.45,
  color: '#5c6b7a',
};

const infoCardStyle = {
  marginTop: '24px',
  padding: '18px 20px',
  borderRadius: '12px',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
};

const infoTitleStyle = {
  margin: 0,
  fontSize: '14px',
  lineHeight: 1.45,
  color: '#050a30',
  fontWeight: 600,
};

const infoTextStyle = {
  margin: '8px 0 0',
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#4a5565',
};

const sourceListStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px 18px',
  marginTop: '12px',
};

const sourceLinkStyle = {
  color: '#155dfc',
  textDecoration: 'underline',
  fontSize: '14px',
  lineHeight: 1.5,
};

const resolveWorldCupLastUpdated = () => {
  const candidates = [
    process.env.REACT_APP_BUILD_TIMESTAMP,
    process.env.REACT_APP_BUILD_TIME,
    process.env.REACT_APP_BUILD_DATE,
    process.env.REACT_APP_LAST_MODIFIED,
    DEFAULT_WORLD_CUP_MODIFIED_DATE,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeSchemaDate(candidate);

    if (!normalized) {
      continue;
    }

    const parsed = new Date(normalized);

    if (!Number.isNaN(parsed.getTime())) {
      return {
        iso: normalized,
        dateOnly: normalized.slice(0, 10),
        label: dateFormatter.format(parsed),
      };
    }
  }

  return {
    iso: '2026-05-22T00:00:00+00:00',
    dateOnly: '2026-05-22',
    label: 'May 22, 2026',
  };
};

const WC_TICKER_ITEMS_TEXT =
  'FIFA World Cup 2026 · June 11 – July 19 · 16 Host Cities · 3 Countries · 48 Teams · 104 Matches · US · Canada · Mexico · Private Jet Charter · Book Early';

const WC_AIRPORT_RAIL_IMG = '/imgs/svg/charterQuote/airport.svg';

function AirportBriefRailGlyph({ kind = 'airport' }) {
  if (kind === 'airport') {
    return <WcAirportBriefRailImg src={WC_AIRPORT_RAIL_IMG} alt="" aria-hidden />;
  }
  if (kind === 'leaf') {
    return (
      <WcAirportBriefRailIcon aria-hidden>
        <Leaf size={22} strokeWidth={2} />
      </WcAirportBriefRailIcon>
    );
  }
  if (kind === 'globe') {
    return (
      <WcAirportBriefRailIcon aria-hidden>
        <Globe2 size={22} strokeWidth={2} />
      </WcAirportBriefRailIcon>
    );
  }
  if (kind === 'star') {
    return (
      <WcAirportBriefRailIcon aria-hidden>
        <Star size={22} strokeWidth={2} />
      </WcAirportBriefRailIcon>
    );
  }
  return null;
}

function AirportBriefNarrCell({ title, paragraph, railKind = 'airport' }) {
  return (
    <WcAirportBriefCell>
      <WcAirportBriefStack>
        <WcAirportBriefHeadRow>
          <AirportBriefRailGlyph kind={railKind} />
          <WcAirportBriefBlockTitle>{title}</WcAirportBriefBlockTitle>
        </WcAirportBriefHeadRow>
        {paragraph ? <WcAirportBriefText>{paragraph}</WcAirportBriefText> : null}
      </WcAirportBriefStack>
    </WcAirportBriefCell>
  );
}

function AirportBriefGatewayCell({ icon, title, bullets }) {
  return (
    <WcAirportBriefCell>
      <WcAirportBriefStack>
        <WcAirportBriefHeadRow>
          <AirportBriefRailGlyph kind={icon} />
          <WcAirportBriefBlockTitle>{title}</WcAirportBriefBlockTitle>
        </WcAirportBriefHeadRow>
        <WcAirportBriefBulletList>
          {bullets.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </WcAirportBriefBulletList>
      </WcAirportBriefStack>
    </WcAirportBriefCell>
  );
}

const stripSortKey = (row, key) => {
  switch (key) {
    case 'hostCity':
      return row.hostCity;
    case 'country':
      return row.country;
    case 'airport':
      return row.airport;
    case 'stage':
      return row.stage;
    default:
      return row.hostCity;
  }
};

const WC_QUOTE_HERO_SIDE_IMG = '/imgs/webp/worldcup/head.webp';

const whyPrivateCardIcons = [Route, Clock, ShieldCheck, Plane];

const WorldCup2026 = () => {
  const [sortKey, setSortKey] = useState('rowOrder');
  const [sortDir, setSortDir] = useState('asc');
  const [pricingTierIndex, setPricingTierIndex] = useState(0);
  const [splashRegIdx, setSplashRegIdx] = useState(0);
  const [groupCharterSplashIdx, setGroupCharterSplashIdx] = useState(0);

  const selectedPricingTier = PRICING_TIERS[pricingTierIndex] ?? PRICING_TIERS[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sortedRows = useMemo(() => {
    const copy = [...HOST_AIRPORT_ROWS];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'rowOrder' || sortKey === 'stageOrder') {
        cmp = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
      } else {
        const va = stripSortKey(a, sortKey);
        const vb = stripSortKey(b, sortKey);
        cmp = `${va}`.localeCompare(`${vb}`, undefined, { sensitivity: 'base' });
      }
      const primary = sortDir === 'asc' ? cmp : -cmp;
      if (primary !== 0) return primary;
      return (a.rowOrder ?? 0) - (b.rowOrder ?? 0);
    });
    return copy;
  }, [sortKey, sortDir]);

  const onHeaderClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const pageUrl = buildAbsoluteUrl(PAGE_PATH);
  const faqItems = WC_FAQ_SPLIT_BLOCKS.flatMap((block) => block.faq ?? []);
  const lastUpdated = resolveWorldCupLastUpdated();
  const logoUrl = buildAbsoluteUrl('/imgs/svg/home/logo.svg');

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: WORLD_CUP_TITLE,
      url: pageUrl,
      description: WORLD_CUP_DESCRIPTION,
      inLanguage: 'en-US',
      datePublished: WORLD_CUP_PUBLISHED_DATE,
      dateModified: lastUpdated.iso,
      publisher: {
        '@type': 'Organization',
        name: 'Airacer Inc.',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
        },
      },
      breadcrumb: {
        '@id': '#breadcrumb',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': '#breadcrumb',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: buildAbsoluteUrl('/'),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: WORLD_CUP_TITLE,
          item: pageUrl,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: WORLD_CUP_TITLE,
      serviceType: 'Air Charter',
      description:
        'On-demand private jet charter flights between all 16 FIFA World Cup 2026 host cities across the United States, Canada, and Mexico. Aircraft categories from light jets to VIP airliners. Group charter and multi-city itineraries available.',
      url: pageUrl,
      provider: {
        '@type': 'Organization',
        name: 'Airacer Inc.',
        url: SITE_URL,
        telephone: '+18002135051',
        email: 'charter@airacer.com',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US',
        },
        memberOf: {
          '@type': 'Organization',
          name: 'National Business Aviation Association',
          url: 'https://nbaa.org',
        },
      },
      areaServed: [
        { '@type': 'Country', name: 'United States' },
        { '@type': 'Country', name: 'Canada' },
        { '@type': 'Country', name: 'Mexico' },
      ],
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '8000',
        highPrice: '90000',
        offerCount: `${PRICING_TIERS.length}`,
        description:
          'One-way charter pricing from $8,000 (light jet, regional) to $90,000+ (heavy jet, international). VIP airliner group programs available.',
      },
      category: 'Private Aviation',
      termsOfService: buildAbsoluteUrl('/termOfUse'),
    },
    buildFAQSchema(faqItems),
    {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'FIFA World Cup 2026',
      startDate: '2026-06-11',
      endDate: '2026-07-19',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: [
        {
          '@type': 'Place',
          name: 'MetLife Stadium',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'East Rutherford',
            addressRegion: 'NJ',
            addressCountry: 'US',
          },
        },
        {
          '@type': 'Place',
          name: 'Multiple host cities',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'US',
          },
        },
      ],
      organizer: {
        '@type': 'Organization',
        name: 'FIFA',
        url: 'https://www.fifa.com',
      },
      description:
        'FIFA World Cup 2026 — 104 matches across 16 host cities in the United States, Canada, and Mexico. June 11 through July 19, 2026.',
      offers: {
        '@type': 'Offer',
        name: 'Private Jet Charter Travel Package',
        url: pageUrl,
        seller: {
          '@type': 'Organization',
          name: 'Airacer Inc.',
        },
      },
    },
  ];

  return (
    <PageRoot>
      <HeaderMeta
        type="worldCup2026"
        jsonLd={jsonLd}
        overrides={{
          canonicalUrl: pageUrl,
        }}
      />

      <WcTickerStrip aria-label="FIFA World Cup 2026 at a glance">
        <WcTickerAccessibleText>
          <WcTickerLabel as="span">Ticker strip:</WcTickerLabel>
          {WC_TICKER_ITEMS_TEXT}
        </WcTickerAccessibleText>
        <WcTickerMarquee aria-hidden>
          <WcTickerTrack>
            <WcTickerSegment>
              <WcTickerLabel as="span">Ticker strip:</WcTickerLabel>
              <span>{WC_TICKER_ITEMS_TEXT}</span>
            </WcTickerSegment>
            <WcTickerSegment>
              <WcTickerLabel as="span">Ticker strip:</WcTickerLabel>
              <span>{WC_TICKER_ITEMS_TEXT}</span>
            </WcTickerSegment>
          </WcTickerTrack>
        </WcTickerMarquee>
      </WcTickerStrip>

      <HeroWrapCharter>
        <WcQuoteHeroBand>
          <WcQuoteHeroHeader>
            <WcQuoteHeroResultsTop aria-label="World Cup private charter overview">
              <WcQuoteHeroMainWrap>
                <WcQuoteHeroTitleBlock>
                  <nav aria-label="Breadcrumb" style={breadcrumbNavStyle}>
                    <a href="/" style={breadcrumbLinkStyle}>
                      Home
                    </a>
                    <span aria-hidden>/</span>
                    <span>{WORLD_CUP_TITLE}</span>
                  </nav>
                  <p className="page-meta" style={pageMetaStyle}>
                    <time dateTime={lastUpdated.dateOnly}>Last updated: {lastUpdated.label}</time>
                  </p>
                  <WcQuoteHeroHeading>Private Jet Charter for World Cup 2026</WcQuoteHeroHeading>
                  <WcQuoteHeroLead>
                    16 host cities. 3 countries. 104 matches. This is your complete guide to private
                    jet charter for FIFA World Cup 2026: pricing, airports, FBO strategy, TFR
                    regulations, group charter, and exactly when to book.
                  </WcQuoteHeroLead>
                  <WcQuoteHeroBadgeGrid aria-label="World Cup charter planning priorities">
                    {HERO_PLAN_FEATURES.map((label) => (
                      <WcQuoteHeroBadge key={label}>{label}</WcQuoteHeroBadge>
                    ))}
                  </WcQuoteHeroBadgeGrid>
                </WcQuoteHeroTitleBlock>
                <WcQuoteHeroActionSlot id="world-cup-hero-actions">
                  <WcQuoteHeroPrimaryCta href="/charterQuote">
                    Request a World Cup Charter Quote
                    <ArrowRight size={18} aria-hidden strokeWidth={2} />
                  </WcQuoteHeroPrimaryCta>
                </WcQuoteHeroActionSlot>
              </WcQuoteHeroMainWrap>
            </WcQuoteHeroResultsTop>
            <WcQuoteHeroBanner
              src={WC_QUOTE_HERO_SIDE_IMG}
              alt="Private jet on ramp at Teterboro Airport ahead of FIFA World Cup 2026 Final at MetLife Stadium"
              loading="eager"
              decoding="async"
            />
          </WcQuoteHeroHeader>
        </WcQuoteHeroBand>
      </HeroWrapCharter>

      <WhyPrivateFleetSection id="why-private">
        <Header
          title={
            <div style={{ maxWidth: '620px' }}>
              Why Private Jet Charter for World Cup 2026 Makes Sense
            </div>
          }
          desc={[
            'Commercial aviation will face extreme pressure across all 16 host cities. Slot restrictions and cancellations will be the norm. Private jet charter for FIFA World Cup 2026 gives you full operational control: your schedule, your routing, your aircraft.',
          ]}
        />
        <WhyPrivateBenefitsGrid>
          {WHY_PRIVATE_CARDS.map((card, index) => {
            const Icon = whyPrivateCardIcons[index] || Plane;
            return (
              <WhyPrivateBenefitCard key={card.title}>
                <Icon aria-hidden strokeWidth={1.75} size={32} color="#155DFC" />
                <WhyPrivateBenefitBody>
                  <WhyPrivateBenefitTitle>{card.title}</WhyPrivateBenefitTitle>
                  <WhyPrivateBenefitText>{card.body}</WhyPrivateBenefitText>
                </WhyPrivateBenefitBody>
              </WhyPrivateBenefitCard>
            );
          })}
        </WhyPrivateBenefitsGrid>
      </WhyPrivateFleetSection>

      <Section id="host-airport-table">
        <Header
          title={
            <div style={{ maxWidth: '620px' }}>
              Private Jet Airports for Every World Cup 2026 Host City
            </div>
          }
          desc={[
            'For the first time, FIFA World Cup spans three nations. Below are the preferred business aviation airports for each host city, chosen to avoid commercial hub congestion.',
          ]}
        />
        <TableWrap>
          <HostAirportTable>
            <thead>
              <tr>
                <th onClick={() => onHeaderClick('hostCity')}>
                  Host City {sortKey === 'hostCity' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => onHeaderClick('country')}>
                  Country {sortKey === 'country' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => onHeaderClick('airport')}>
                  Preferred Private Jet Airport{' '}
                  {sortKey === 'airport' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => onHeaderClick('stageOrder')}>
                  Stage {sortKey === 'stageOrder' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr
                  key={`${r.rowOrder}-${r.hostCity}`}
                  className={r.highlightRow ? 'highlight-row' : undefined}
                >
                  <td>{r.hostCity}</td>
                  <td>{r.country}</td>
                  <td>{r.airport}</td>
                  <td>
                    <StageTag $tone={r.stageTone}>{r.stage}</StageTag>
                  </td>
                </tr>
              ))}
            </tbody>
          </HostAirportTable>
        </TableWrap>
        <MobileTableAccordion>
          <Collapse accordion bordered={false}>
            {sortedRows.map((r) => (
              <Collapse.Panel
                header={`${r.hostCity} — ${r.country}`}
                key={`${r.rowOrder}-${r.hostCity}`}
              >
                <p>
                  <strong>Preferred airport:</strong> {r.airport}
                </p>
                <p>
                  <strong>Stage:</strong> <StageTag $tone={r.stageTone}>{r.stage}</StageTag>
                </p>
              </Collapse.Panel>
            ))}
          </Collapse>
        </MobileTableAccordion>
      </Section>

      <Section id="pricing">
        <Header
          title={
            <div style={{ maxWidth: '620px' }}>
              Private Jet Charter Costs for FIFA World Cup 2026
            </div>
          }
          desc={[
            'Pricing during FIFA World Cup 2026 will not follow standard market patterns. Aircraft scarcity, FBO surcharges, and repositioning complexity push rates higher, especially during knockout rounds.',
          ]}
        />
        <WcPricingInteractive aria-label="World Cup charter cost by aircraft category">
          <WcPricingCardColumn>
            {PRICING_TIERS.map((tier, index) => {
              const active = index === pricingTierIndex;
              return (
                <WcPricingTierBtn
                  key={tier.id}
                  $active={active}
                  aria-pressed={active}
                  onClick={() => setPricingTierIndex(index)}
                >
                  <WcPricingTopRow>
                    <WcPricingTierHeading $active={active}>{tier.headline}</WcPricingTierHeading>
                    <WcPricingTierPriceStr $active={active}>
                      {tier.priceRange}
                    </WcPricingTierPriceStr>
                  </WcPricingTopRow>
                  <WcPricingTierSubtitle $active={active}>{tier.subtitle}</WcPricingTierSubtitle>
                  <WcPricingTierBody $active={active}>{tier.description}</WcPricingTierBody>
                </WcPricingTierBtn>
              );
            })}
          </WcPricingCardColumn>
          <WcPricingVisual>
            <img
              src={selectedPricingTier.image}
              alt={`Stylised illustration — ${selectedPricingTier.headline}`}
              decoding="async"
              loading="lazy"
            />
          </WcPricingVisual>
        </WcPricingInteractive>
        {/* <WcPricingTournamentAlert>
          <strong>Tournament Pricing Alert:</strong> {WC_PRICING_TOURNAMENT_ALERT_BODY}
        </WcPricingTournamentAlert> */}
        <div style={infoCardStyle}>
          <p style={infoTitleStyle}>Broker disclosure</p>
          <p style={infoTextStyle}>
            Airacer is an FAA-registered air charter broker operating under DOT authorization.
            Charter pricing depends on aircraft category, routing, parking, crew duty limits, and
            special-event surcharges tied to World Cup demand.
          </p>
        </div>
      </Section>

      <WcFlyingCloudsBanner
        src="/imgs/webp/worldcup/full.webp"
        alt="Private jet charter flying between World Cup 2026 host cities across the US, Canada, and Mexico"
        loading="lazy"
        decoding="async"
      />

      <Section id="best-airports-wc">
        <Header
          title={
            <div style={{ maxWidth: 640 }}>
              Best Airports for Private Jets During World Cup 2026
            </div>
          }
          desc={BEST_AIRPORTS_WC2026_HEADER}
        />
        <WcAirportBriefCosts>
          {BEST_AIRPORTS_WC2026_ROWS.map((row, ri) => (
            <WcAirportBriefGridRow key={`airport-row-${ri}`}>
              <AirportBriefNarrCell title={row.left.title} paragraph={row.left.desc} />
              {row.right ? (
                <AirportBriefNarrCell title={row.right.title} paragraph={row.right.desc} />
              ) : (
                <WcAirportBriefGhost />
              )}
            </WcAirportBriefGridRow>
          ))}
          <WcAirportBriefGridRow>
            <AirportBriefGatewayCell {...BEST_AIRPORTS_WC2026_GATEWAYS.canada} />
            <AirportBriefGatewayCell {...BEST_AIRPORTS_WC2026_GATEWAYS.mexico} />
          </WcAirportBriefGridRow>
          <WcAirportBriefGridRow>
            <WcAirportBriefFboCell>
              <WcAirportBriefStack>
                <WcAirportBriefHeadRow>
                  <AirportBriefRailGlyph kind={BEST_AIRPORTS_WC2026_FBO.icon} />
                  <WcAirportBriefBlockTitle id="wc-fbo-capacity-brief-title">
                    {BEST_AIRPORTS_WC2026_FBO.title}
                  </WcAirportBriefBlockTitle>
                </WcAirportBriefHeadRow>
                <WcAirportBriefText>{BEST_AIRPORTS_WC2026_FBO.desc}</WcAirportBriefText>
              </WcAirportBriefStack>
            </WcAirportBriefFboCell>
          </WcAirportBriefGridRow>
        </WcAirportBriefCosts>
      </Section>

      <Section id="faa-cross-border-splash">
        <Header
          title={
            <div style={{ maxWidth: 720 }}>
              FAA Regulations, TFRs, and Cross-Border Rules for Private Jets During World Cup 2026
            </div>
          }
          desc={WC_REG_SPLASH_HEADER}
        />
        <WcRegSplashSplit>
          <WcRegSplashFigure>
            <img
              src="/imgs/webp/worldcup/FAA.webp"
              alt="FBO terminal at Van Nuys Airport near Los Angeles World Cup 2026 Semifinal venue"
              loading="lazy"
              decoding="async"
            />
          </WcRegSplashFigure>
          <WcRegSplashAccordion>
            {WC_REG_SPLASH_ITEMS.map((item, idx) => {
              const active = splashRegIdx === idx;
              return (
                <WcRegSplashItem key={item.title} $open={active}>
                  <WcRegSplashHead
                    aria-expanded={active}
                    id={`wc-reg-splash-head-${idx}`}
                    aria-controls={`wc-reg-splash-panel-${idx}`}
                    onClick={() => setSplashRegIdx(active ? -1 : idx)}
                  >
                    <WcRegSplashHeadMain>
                      {active ? (
                        <WcRegSplashNumBadge>
                          {String(idx + 1).padStart(2, '0')}
                        </WcRegSplashNumBadge>
                      ) : (
                        <WcRegSplashNumPlain>
                          {String(idx + 1).padStart(2, '0')}
                        </WcRegSplashNumPlain>
                      )}
                      <span>{item.title}</span>
                    </WcRegSplashHeadMain>
                    <WcRegSplashChevron $open={active}>
                      <ChevronDown size={22} strokeWidth={2.25} />
                    </WcRegSplashChevron>
                  </WcRegSplashHead>
                  <WcRegSplashCollapse $open={active}>
                    <WcRegSplashCollapseInner aria-hidden={!active}>
                      <WcRegSplashBody
                        id={`wc-reg-splash-panel-${idx}`}
                        role="region"
                        aria-labelledby={`wc-reg-splash-head-${idx}`}
                        $open={active}
                      >
                        <WcRegSplashBodyText $visible={active}>{item.body}</WcRegSplashBodyText>
                      </WcRegSplashBody>
                    </WcRegSplashCollapseInner>
                  </WcRegSplashCollapse>
                </WcRegSplashItem>
              );
            })}
            <WcRegSplashAccordionSpacer />
          </WcRegSplashAccordion>
        </WcRegSplashSplit>
        <div style={infoCardStyle}>
          <p style={infoTitleStyle}>Authority sources for routing and compliance</p>
          <div style={sourceListStyle}>
            {AUTHORITY_LINKS.map((link) => (
              <a
                href={link.href}
                key={link.href}
                rel="noopener noreferrer"
                style={sourceLinkStyle}
                target="_blank"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </Section>

      <Section id="when-to-book-charter-panel">
        <ImgDescCard
          isLeft={false}
          blendImageBoundary
          imgSrc="/imgs/webp/worldcup/book.webp"
          imgAlt="Private jet departing Dallas Addison Airport for FIFA World Cup 2026 Semifinal"
          title="When to Book Your Private Jet Charter for World Cup 2026"
          desc={WC_WHEN_BOOK_INTRO}
          gap="clamp(14px, 2.8vw, 28px)"
        >
          <FAQAccordion QuestionLists={WC_WHEN_BOOK_ACCORDION} />
        </ImgDescCard>
      </Section>

      <WcPlanningBannerSection id="wc-planning-travel-banner">
        <WcPlanningBannerWrap>
          <ImageOverlay $img="/imgs/webp/worldcup/plan.webp">
            <WcPlanningBannerBgAlt>{WC_PLANNING_BANNER.imageAlt}</WcPlanningBannerBgAlt>
            <h2>{WC_PLANNING_BANNER.title}</h2>
            <p className="wc-planning-lead">{WC_PLANNING_BANNER.lead}</p>
            <div className="wc-planning-cta-row">
              <BtnPrimary href="/charterQuote">
                Request a World Cup Charter Quote
                <ArrowRight size={18} aria-hidden strokeWidth={2} />
              </BtnPrimary>
            </div>
          </ImageOverlay>
        </WcPlanningBannerWrap>
      </WcPlanningBannerSection>

      <Section id="group-charter-flights" aria-labelledby="wc-group-charter-heading">
        <GroupCharterSplashIntro>
          <GroupCharterSplashTitle id="wc-group-charter-heading">
            {GROUP_CHARTER_SPLASH_TITLE}
          </GroupCharterSplashTitle>
          <GroupCharterSplashLead>{GROUP_CHARTER_SPLASH_INTRO}</GroupCharterSplashLead>
        </GroupCharterSplashIntro>
        <GroupCharterSplashDivider aria-hidden />
        <GroupCharterSplashSplit>
          <WcRegSplashFigure>
            <img
              src="/imgs/webp/jetCard/sunset.webp"
              alt="Private jet on an airport runway at sunset"
              loading="lazy"
              decoding="async"
            />
          </WcRegSplashFigure>
          <WcRegSplashAccordion>
            {GROUP_CHARTER_SPLASH_ITEMS.map((item, idx) => {
              const active = groupCharterSplashIdx === idx;
              return (
                <WcRegSplashItem key={item.title} $open={active}>
                  <WcRegSplashHead
                    aria-expanded={active}
                    id={`wc-group-charter-head-${idx}`}
                    aria-controls={`wc-group-charter-panel-${idx}`}
                    onClick={() => setGroupCharterSplashIdx(active ? -1 : idx)}
                  >
                    <WcRegSplashHeadMain>
                      {active ? (
                        <WcRegSplashNumBadge>
                          {String(idx + 1).padStart(2, '0')}
                        </WcRegSplashNumBadge>
                      ) : (
                        <WcRegSplashNumPlain>
                          {String(idx + 1).padStart(2, '0')}
                        </WcRegSplashNumPlain>
                      )}
                      <span>{item.title}</span>
                    </WcRegSplashHeadMain>
                    <WcRegSplashChevron $open={active}>
                      <ChevronDown size={22} strokeWidth={2.25} />
                    </WcRegSplashChevron>
                  </WcRegSplashHead>
                  <WcRegSplashCollapse $open={active}>
                    <WcRegSplashCollapseInner aria-hidden={!active}>
                      <WcRegSplashBody
                        id={`wc-group-charter-panel-${idx}`}
                        role="region"
                        aria-labelledby={`wc-group-charter-head-${idx}`}
                        $open={active}
                      >
                        <WcRegSplashBodyText $visible={active}>{item.body}</WcRegSplashBodyText>
                      </WcRegSplashBody>
                    </WcRegSplashCollapseInner>
                  </WcRegSplashCollapse>
                </WcRegSplashItem>
              );
            })}
            <WcRegSplashAccordionSpacer />
          </WcRegSplashAccordion>
        </GroupCharterSplashSplit>
      </Section>

      <Section id="wc-faq-visual-panels" aria-label={WC_FAQ_SPLIT_PANEL_TITLE}>
        <WcFaqTwinStack>
          {WC_FAQ_SPLIT_BLOCKS.map((block) => (
            <WcFaqTwinRow key={block.imgSrc}>
              <WcFaqTwinFigure>
                <img src={block.imgSrc} alt={block.imgAlt} loading="lazy" decoding="async" />
              </WcFaqTwinFigure>
              <WcFaqTwinPanel>
                <WcFaqTwinHeading>{WC_FAQ_SPLIT_PANEL_TITLE}</WcFaqTwinHeading>
                <FAQAccordion QuestionLists={block.faq} />
              </WcFaqTwinPanel>
            </WcFaqTwinRow>
          ))}
        </WcFaqTwinStack>
      </Section>

      <BottomCta id="world-cup-quote">
        <h2>Private Jet Charter for World Cup 2026 — Aircraft Filling Now</h2>
        <p>
          FIFA World Cup 2026 runs June 11 through July 19. Knockout round aircraft and Teterboro
          FBO slots for Final week are already being reserved. The earlier you lock in, the more
          options you have across aircraft category, airport access, and route flexibility.
        </p>
        <BtnPrimary href="/charterQuote">Get Your Charter Quote →</BtnPrimary>
      </BottomCta>

      <Footer />

      {/* <StickyMobileCta>
        <BtnPrimary href="#world-cup-quote" style={{ flex: 1, padding: '12px 16px', fontSize: 14 }}>
          Request a Quote
        </BtnPrimary>
        <BtnHeroDark
          as={Link}
          to="/contactUs"
          style={{ flex: 1, padding: '12px 16px', fontSize: 14 }}
        >
          Contact Us
        </BtnHeroDark>
      </StickyMobileCta> */}
    </PageRoot>
  );
};

export default WorldCup2026;
